<div class="diagnostic-container">
  <div class="diagnostic-header">
    <div class="header-content">
      <h2>topic: {{ diagnosticData?.centerTopic?.label }}</h2>
    </div>
    <button class="btn btn--bare" (click)="close()" aria-label="Close">×</button>
  </div>

  <div class="diagnostic-content" *ngIf="!loading && !error && diagnosticData">
    <!-- Coverage Section -->
    <div class="main-section">
      <h3 class="section-heading">
        Coverage
        <span 
          class="badge score-tag" 
          *ngIf="diagnosticData.centerTopic.coverageScore !== undefined"
          [class.badge--success]="getCoverageLevel() === 'high'"
          [class.badge--warning]="getCoverageLevel() === 'med'"
          [class.badge--danger]="getCoverageLevel() === 'low'"
        >
          {{ (diagnosticData.centerTopic.coverageScore * 100).toFixed(0) }}%
        </span>
      </h3>
      <!-- Pages Covering This Topic -->
      <div class="section">
        <div class="section-label">
          <div class="section-label-header">
            <div class="section-label-title">Breadth</div>
            <span 
              class="badge score-tag" 
              *ngIf="diagnosticData.centerTopic.breadthScore !== undefined"
              [class.badge--success]="getBreadthLevel() === 'high'"
              [class.badge--warning]="getBreadthLevel() === 'med'"
              [class.badge--danger]="getBreadthLevel() === 'low'"
            >
              {{ (diagnosticData.centerTopic.breadthScore * 100).toFixed(0) }}%
            </span>
          </div>
          <div class="section-label-explanation">Measures how many pages cover this topic. Higher breadth = broader topic coverage across your site.</div>
        </div>
        <div class="section-content">
          <div class="page-count-display">{{ diagnosticData.centerTopic.pageCount || 0 }} pages</div>
          <div class="section-description">
            This domain directly covers the topic <strong>{{ diagnosticData.centerTopic.label }}</strong> and merged variations on {{ diagnosticData.centerTopic.pageCount || 0 }} different pages, contributing to a <strong>{{ getBreadthLevel() }}</strong> Breadth score.
          </div>
          <div class="topics-list tags-list">
            <div 
              *ngFor="let topic of diagnosticData.coverage.directlyCoveredTopics" 
              class="badge topic-tag-item"
              (click)="toggleBreadthTopicUrls(topic.parentTopicSlug)"
              [class.expanded]="expandedBreadthTopicSlug === topic.parentTopicSlug"
              [class.badge--accent]="expandedBreadthTopicSlug === topic.parentTopicSlug"
            >
              <span class="topic-tag-label">{{ topic.label }}</span>
              <span class="badge meta-tag">{{ topic.urlCount }} pages</span>
              <span class="badge meta-tag">{{ topic.coreCount || 0 }} core</span>
              <span class="badge meta-tag">{{ topic.mentionCount || 0 }} mentions</span>
              <span class="chevron">›</span>
            </div>
            <div *ngIf="diagnosticData.coverage.directlyCoveredTopics.length === 0" class="empty-state">
              No directly covered topics found.
            </div>
          </div>
          <!-- Expanded URLs for Breadth -->
          <div *ngIf="expandedBreadthTopicSlug" class="expanded-urls">
            <div class="expanded-urls-header">
              <span>URLs for {{ getTopicLabel(expandedBreadthTopicSlug) }}</span>
              <button class="close-expanded" (click)="expandedBreadthTopicSlug = null">×</button>
            </div>
            <div class="urls-subsection">
              <div class="urls-subsection-header">
                <span>Core pages</span>
                <span class="badge meta-tag">{{ getCoreCount(expandedBreadthTopicSlug) }} total</span>
              </div>
              <div class="urls-list">
                <div *ngFor="let url of getCorePages(expandedBreadthTopicSlug)" class="url-item url-item-core">
                  <a [href]="url.url" target="_blank" class="url-link">{{ extractUrlPath(url.url) }}</a>
                  <span class="url-meta">Intent: {{ formatSeoIntent(url.seoIntentScore) }}</span>
                  <span class="url-meta">Specificity: {{ formatSpecificity(url.specificityScore) }}</span>
                  <span class="url-meta">Weight: {{ url.weight.toFixed(2) }}</span>
                  <span class="url-meta">Salience: {{ url.salience.toFixed(2) }}</span>
                  <span class="url-meta">Density: {{ (url.relativeDensity * 100).toFixed(1) }}%</span>
                  <span class="url-meta">Margin: {{ formatRatio(url.marginToRunnerUp) }}</span>
                  <span class="url-meta">Ownership: {{ formatRatio(url.ownershipScore) }}</span>
                  <span class="url-meta">Contrast: {{ formatRatio(url.contrastScore) }}</span>
                  <span class="url-meta">Prevalence: {{ formatRatio(url.topicPrevalence) }}</span>
                  <span class="url-meta" *ngIf="url.isAmbientLike">Ambient-like</span>
                </div>
                <div *ngIf="getCorePages(expandedBreadthTopicSlug).length === 0" class="empty-state">
                  No core pages yet. Mentions are still useful for topic ideation.
                </div>
              </div>
            </div>
            <button class="toggle-mentions" (click)="toggleMentions(expandedBreadthTopicSlug)">
              {{ areMentionsExpanded(expandedBreadthTopicSlug) ? 'Hide mentions' : 'Show mentions' }}
              ({{ getMentionCount(expandedBreadthTopicSlug) }})
            </button>
            <div *ngIf="areMentionsExpanded(expandedBreadthTopicSlug)" class="urls-subsection">
              <div class="urls-subsection-header">
                <span>Mentions</span>
                <span class="badge meta-tag">Fallback inventory</span>
              </div>
              <div class="urls-list">
                <div *ngFor="let url of getMentionPages(expandedBreadthTopicSlug)" class="url-item">
                  <a [href]="url.url" target="_blank" class="url-link">{{ extractUrlPath(url.url) }}</a>
                  <span class="url-meta">Intent: {{ formatSeoIntent(url.seoIntentScore) }}</span>
                  <span class="url-meta">Specificity: {{ formatSpecificity(url.specificityScore) }}</span>
                  <span class="url-meta">Weight: {{ url.weight.toFixed(2) }}</span>
                  <span class="url-meta">Salience: {{ url.salience.toFixed(2) }}</span>
                  <span class="url-meta">Density: {{ (url.relativeDensity * 100).toFixed(1) }}%</span>
                  <span class="url-meta">Margin: {{ formatRatio(url.marginToRunnerUp) }}</span>
                  <span class="url-meta">Ownership: {{ formatRatio(url.ownershipScore) }}</span>
                  <span class="url-meta">Contrast: {{ formatRatio(url.contrastScore) }}</span>
                  <span class="url-meta">Prevalence: {{ formatRatio(url.topicPrevalence) }}</span>
                  <span class="url-meta" *ngIf="url.isAmbientLike">Ambient-like</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Related Topics (Depth) -->
      <div class="section">
        <div class="section-label">
          <div class="section-label-header">
            <div class="section-label-title">Depth</div>
            <span 
              class="badge score-tag" 
              *ngIf="diagnosticData.centerTopic.depthScore !== undefined"
              [class.badge--success]="getDepthLevel() === 'high'"
              [class.badge--warning]="getDepthLevel() === 'med'"
              [class.badge--danger]="getDepthLevel() === 'low'"
            >
              {{ (diagnosticData.centerTopic.depthScore * 100).toFixed(0) }}%
            </span>
          </div>
          <div class="section-label-explanation">Measures coverage of related subtopics. Higher depth = comprehensive topic domain expertise.</div>
        </div>
        <div class="section-content">
          <div class="page-count-display">{{ getTotalRelatedPages() }} pages</div>
          <div class="section-description">
            This domain covers <strong>{{ diagnosticData.coverage.relatedTopics.length || 0 }} related subtopics</strong> with {{ getTotalRelatedPages() }} total pages, contributing to a <strong>{{ getDepthLevel() }}</strong> Depth score.
          </div>
          <div class="section-title">top X covered related topics</div>
          <div class="topics-list tags-list">
            <div 
              *ngFor="let topic of diagnosticData.coverage.relatedTopics" 
              class="badge topic-tag-item"
              (click)="toggleDepthTopicUrls(topic.parentTopicSlug)"
              [class.expanded]="expandedDepthTopicSlug === topic.parentTopicSlug"
              [class.badge--accent]="expandedDepthTopicSlug === topic.parentTopicSlug"
            >
              <span class="topic-tag-label">{{ topic.label }}</span>
              <span class="badge meta-tag">{{ topic.urlCount }} pages</span>
              <span class="badge meta-tag">{{ topic.coreCount || 0 }} core</span>
              <span class="badge meta-tag">{{ topic.mentionCount || 0 }} mentions</span>
              <span class="badge meta-tag">{{ (topic.similarity * 100).toFixed(0) }}% similar</span>
              <span class="chevron">›</span>
            </div>
            <div *ngIf="diagnosticData.coverage.relatedTopics.length === 0" class="empty-state">
              No related topics found.
            </div>
          </div>
          <!-- Expanded URLs for Depth -->
          <div *ngIf="expandedDepthTopicSlug" class="expanded-urls">
            <div class="expanded-urls-header">
              <span>URLs for {{ getTopicLabel(expandedDepthTopicSlug) }}</span>
              <button class="close-expanded" (click)="expandedDepthTopicSlug = null">×</button>
            </div>
            <div class="urls-subsection">
              <div class="urls-subsection-header">
                <span>Core pages</span>
                <span class="badge meta-tag">{{ getCoreCount(expandedDepthTopicSlug) }} total</span>
              </div>
              <div class="urls-list">
                <div *ngFor="let url of getCorePages(expandedDepthTopicSlug)" class="url-item url-item-core">
                  <a [href]="url.url" target="_blank" class="url-link">{{ extractUrlPath(url.url) }}</a>
                  <span class="url-meta">Intent: {{ formatSeoIntent(url.seoIntentScore) }}</span>
                  <span class="url-meta">Specificity: {{ formatSpecificity(url.specificityScore) }}</span>
                  <span class="url-meta">Weight: {{ url.weight.toFixed(2) }}</span>
                  <span class="url-meta">Salience: {{ url.salience.toFixed(2) }}</span>
                  <span class="url-meta">Density: {{ (url.relativeDensity * 100).toFixed(1) }}%</span>
                  <span class="url-meta">Margin: {{ formatRatio(url.marginToRunnerUp) }}</span>
                  <span class="url-meta">Ownership: {{ formatRatio(url.ownershipScore) }}</span>
                  <span class="url-meta">Contrast: {{ formatRatio(url.contrastScore) }}</span>
                  <span class="url-meta">Prevalence: {{ formatRatio(url.topicPrevalence) }}</span>
                  <span class="url-meta" *ngIf="url.isAmbientLike">Ambient-like</span>
                </div>
                <div *ngIf="getCorePages(expandedDepthTopicSlug).length === 0" class="empty-state">
                  No core pages for this related topic.
                </div>
              </div>
            </div>
            <button class="toggle-mentions" (click)="toggleMentions(expandedDepthTopicSlug)">
              {{ areMentionsExpanded(expandedDepthTopicSlug) ? 'Hide mentions' : 'Show mentions' }}
              ({{ getMentionCount(expandedDepthTopicSlug) }})
            </button>
            <div *ngIf="areMentionsExpanded(expandedDepthTopicSlug)" class="urls-subsection">
              <div class="urls-subsection-header">
                <span>Mentions</span>
              </div>
              <div class="urls-list">
                <div *ngFor="let url of getMentionPages(expandedDepthTopicSlug)" class="url-item">
                  <a [href]="url.url" target="_blank" class="url-link">{{ extractUrlPath(url.url) }}</a>
                  <span class="url-meta">Intent: {{ formatSeoIntent(url.seoIntentScore) }}</span>
                  <span class="url-meta">Specificity: {{ formatSpecificity(url.specificityScore) }}</span>
                  <span class="url-meta">Weight: {{ url.weight.toFixed(2) }}</span>
                  <span class="url-meta">Salience: {{ url.salience.toFixed(2) }}</span>
                  <span class="url-meta">Density: {{ (url.relativeDensity * 100).toFixed(1) }}%</span>
                  <span class="url-meta">Margin: {{ formatRatio(url.marginToRunnerUp) }}</span>
                  <span class="url-meta">Ownership: {{ formatRatio(url.ownershipScore) }}</span>
                  <span class="url-meta">Contrast: {{ formatRatio(url.contrastScore) }}</span>
                  <span class="url-meta">Prevalence: {{ formatRatio(url.topicPrevalence) }}</span>
                  <span class="url-meta" *ngIf="url.isAmbientLike">Ambient-like</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Focus Section -->
    <div class="main-section">
      <h3 class="section-heading">
        Focus
        <span 
          class="badge score-tag" 
          *ngIf="diagnosticData.focus.focusScore !== undefined"
          [class.badge--success]="getFocusLevel() === 'high'"
          [class.badge--warning]="getFocusLevel() === 'med'"
          [class.badge--danger]="getFocusLevel() === 'low'"
        >
          {{ (diagnosticData.focus.focusScore * 100).toFixed(0) }}%
        </span>
      </h3>
      <div class="section">
        <div class="section-label">
          <div class="section-label-explanation">Measures how well-optimized each page is for the topic cluster. Higher focus = pages deeply cover the topic with related subtopics, not just mentioning it in passing.</div>
        </div>
        <div class="section-content">
          <div class="section-title">Most Focused Pages</div>
          <div class="pages-list">
            <div *ngFor="let page of getFocusedPages()" class="page-item">
              <a [href]="page.url" target="_blank" class="page-url">{{ extractUrlPath(page.url) }}</a>
              <span class="page-meta">
                Density: {{ (page.relativeDensity * 100).toFixed(1) }}% • 
                Weight: {{ page.weight.toFixed(2) }}
              </span>
            </div>
          </div>
          <button 
            *ngIf="diagnosticData.focus.mostFocusedPages.length > 5 && !showAllFocusedPages"
            class="btn"
            (click)="showAllFocusedPages = true"
          >
            Load more ({{ diagnosticData.focus.mostFocusedPages.length - 5 }} more)
          </button>
          <div *ngIf="diagnosticData.focus.mostFocusedPages.length === 0" class="empty-state">
            No focused pages found.
          </div>
        </div>
      </div>
    </div>

    <!-- Links Section -->
    <div class="main-section">
      <h3 class="section-heading">
        Links
        <span 
          class="badge score-tag" 
          *ngIf="diagnosticData.links.linkScore !== undefined"
          [class.badge--success]="getLinkLevel() === 'high'"
          [class.badge--warning]="getLinkLevel() === 'med'"
          [class.badge--danger]="getLinkLevel() === 'low'"
        >
          {{ (diagnosticData.links.linkScore * 100).toFixed(0) }}%
        </span>
      </h3>
      <div class="section">
        <div class="section-label">
          <div class="section-label-explanation">Measures contextual internal links that mention this topic. Higher link score = stronger internal linking structure supporting topic authority.</div>
        </div>
        <div class="section-content">
          <div class="page-count-display" *ngIf="diagnosticData.links.contextualLinkCount !== undefined">
            {{ diagnosticData.links.contextualLinkCount }} contextual links
          </div>
          <div class="section-title">Top Contextual Links</div>
          <div class="links-list">
            <div *ngFor="let link of getContextualLinks()" class="link-item">
              <div class="link-source">
                <a [href]="link.sourceUrl" target="_blank">{{ extractUrlPath(link.sourceUrl) }}</a>
              </div>
              <div class="link-arrow">→</div>
              <div class="link-target">
                <a [href]="link.targetUrl" target="_blank">{{ extractUrlPath(link.targetUrl) }}</a>
              </div>
              <div class="link-anchor" *ngIf="link.anchorText">
                "{{ link.anchorText }}"
              </div>
            </div>
          </div>
          <button 
            *ngIf="diagnosticData.links.topContextualLinks.length > 5 && !showAllContextualLinks"
            class="btn"
            (click)="showAllContextualLinks = true"
          >
            Load more ({{ diagnosticData.links.topContextualLinks.length - 5 }} more)
          </button>
          <div *ngIf="diagnosticData.links.topContextualLinks.length === 0" class="empty-state">
            No contextual links found.
          </div>
        </div>
      </div>
    </div>

    <!-- Strength Section -->
    <div class="main-section">
      <h3 class="section-heading">
        Strength
        <span 
          class="badge score-tag" 
          *ngIf="diagnosticData.strength.strengthScore !== undefined"
          [class.badge--success]="getStrengthLevel() === 'high'"
          [class.badge--warning]="getStrengthLevel() === 'med'"
          [class.badge--danger]="getStrengthLevel() === 'low'"
        >
          {{ (diagnosticData.strength.strengthScore * 100).toFixed(0) }}%
        </span>
      </h3>

      <!-- Strongest Pages -->
      <div class="section">
        <div class="section-label">Strongest pages (later, ordered by traffic)</div>
        <div class="section-content">
          <div *ngFor="let page of diagnosticData.coverage.strongestPages" class="page-item">
            <a [href]="page.url" target="_blank" class="page-url">{{ extractUrlPath(page.url) }}</a>
            <div class="topic-tags" *ngIf="page.topicTags.length > 0">
              <span 
                *ngFor="let tag of page.topicTags.slice(0, 3)" 
                class="badge topic-tag"
              >
                {{ tag.label }}
              </span>
              <span *ngIf="page.topicTags.length > 3" class="badge badge--accent topic-tag">
                +{{ page.topicTags.length - 3 }} more
              </span>
            </div>
          </div>
          <div *ngIf="diagnosticData.coverage.strongestPages.length === 0" class="empty-state">
            No pages found.
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="loading" *ngIf="loading">
    <p>Loading diagnostic data...</p>
  </div>

  <div class="error" *ngIf="error">
    <p>{{ error }}</p>
    <button class="btn" (click)="loadDiagnosticData()">Retry</button>
  </div>
</div>
